import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ActivatedRoute, RouterLink, RouterLinkActive, RouterOutlet, Router } from '@angular/router';
import { ModalService } from '../../services/modal.service';

import { FileUploader, FileUploadModule } from 'ng2-file-upload';
import { forkJoin, Observable } from 'rxjs';
import { I18nTranslator } from '../../i18n/translate';
import { Country, Language } from '../../models/lists.model';
import { Organisation } from '../../models/organisation.model';
import { User } from '../../models/user.model';
import { ListsService } from '../../services/lists.service';
import { OrganisationService } from '../../services/organisation.service';
import { UserService } from '../../services/user.service';
import { hasPermission, showErrorMessage, sortingString, toBoolean } from '../../utils/utils';
import { TranslatePipe, TranslateWithPunctuationPipe } from '../../pipes/translate.pipe';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

@Component({
  standalone: true,
  selector: 'organisation-edit',
  templateUrl: 'organisation-edit.component.html',
  imports: [RouterLink, RouterLinkActive, RouterOutlet, TranslatePipe, TranslateWithPunctuationPipe, CommonModule, FormsModule, FileUploadModule],
  providers: [OrganisationService, UserService],
})
export class OrganisationEditComponent implements OnInit, OnDestroy {
  @Input() organisation: Organisation;
  @Output() close = new EventEmitter();
  error: any;
  sub: any;
  edited = false;
  countries: Country[];
  languagesIndex: Map<number, Language>;

  organisationId: number;

  sectorOwners: User[];
  negoceOwners: User[];

  showManagers = false;
  managers: User[];

  country: Country;

  public uploader: FileUploader = new FileUploader({ url: '' });

  constructor(
    private organisationService: OrganisationService,
    private listService: ListsService,
    private userService: UserService,
    private i18nTranslator: I18nTranslator,
    private modal: ModalService,
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  goBack(savedOrganisation: Organisation = null) {
    this.close.emit(savedOrganisation);
    this.router.navigate(['/organisations']);
    return false;
  }

  handleCountries(countries: Country[]) {
    this.countries = countries.sort(sortingString('label'));
    // France en haut
    const fr = this.countries.find((c) => c.code === 'FR');
    if (fr) {
      this.countries.splice(this.countries.indexOf(fr), 1);
      this.countries.unshift(fr);
    }
  }

  handleLanguages(languages: Language[]) {
    languages = languages.sort(sortingString('label'));
    this.languagesIndex = new Map<number, Language>();
    for (const language of languages) {
      this.languagesIndex.set(language.id, language);
    }
  }

  getManagers() {
    // TODO handle error
    this.userService.getUsers(this.organisationId, ['ROLE_MANAGER']).subscribe((m) => {
      this.managers = m;
      this.showManagers = true;
    });
  }

  getSectorOwners() {
    // TODO handle error
    this.userService.getUsers(null, ['ROLE_SECTOR', 'ROLE_ADMIN']).subscribe((users) => {
      this.sectorOwners = users;
      if (!this.edited && this.sectorOwners && this.sectorOwners.length === 1) {
        this.organisation.sectorOwner = this.sectorOwners[0];
      }
    });
  }

  getNegoceOwners() {
    // TODO handle error
    this.userService.getUsers(null, ['ROLE_NEGOCE']).subscribe((users) => {
      this.negoceOwners = users;
    });
  }

  getSectorOwnerMe() {
    // TODO handle error
    this.userService.getMe().subscribe((me) => {
      this.sectorOwners = [me];
      if (!this.edited) {
        this.organisation.sectorOwner = me;
      }
    });
  }

  save() {
    this.organisation.hasBeApi = toBoolean(this.organisation.hasBeApi);
    this.organisation.hasI18n = toBoolean(this.organisation.hasI18n);

    // Build payload without mutating the form-bound model. Sending
    // {id: null} for an optional FK trips Hibernate's transient instance
    // check, so we omit the FK entirely when no id is selected.
    const payload: Organisation = { ...this.organisation };
    if (!payload.negoceOwner || !payload.negoceOwner.id) {
      payload.negoceOwner = null;
    }

    this.organisationService.save(payload).subscribe(
      (organisation) => {
        this.organisation = organisation;
        this.goBack(organisation);
      },
      (error) => this.handleSaveError(error),
    );
  }

  ngOnInit() {
    this.sub = this.route.params.subscribe((params) => {
      if (hasPermission(['ROLE_ADMIN', 'ROLE_SECTOR'], ['/home'], this.router)) {
        if (this.route.snapshot.url[1].path === 'edit') {
          this.organisationId = +params['organisationId'];
          this.edited = true;
          this.organisationService.getOrganisation(this.organisationId).subscribe(
            (organisation) => {
              this.organisation = organisation;
              if (!this.organisation.country || !this.organisation.country.id) {
                this.organisation.country = new Country();
                this.organisation.country.id = null;
              } else {
                this.onChangeCountry(this.organisation.country.id);
              }

              if (!this.organisation.defaultLanguageGui || !this.organisation.defaultLanguageGui.id) {
                this.organisation.defaultLanguageGui = new Language();
                this.organisation.defaultLanguageGui.id = null;
              }
              if (!this.organisation.defaultLanguagePdf || !this.organisation.defaultLanguagePdf.id) {
                this.organisation.defaultLanguagePdf = new Language();
                this.organisation.defaultLanguagePdf.id = null;
              }

              if (!this.organisation.sectorOwner || !this.organisation.sectorOwner.id) {
                this.organisation.sectorOwner = new User();
                this.organisation.sectorOwner.id = null;
              }
              if (!this.organisation.negoceOwner || !this.organisation.negoceOwner.id) {
                this.organisation.negoceOwner = new User();
                this.organisation.negoceOwner.id = null;
              }
            },
            (error) => this.handleGetError(error),
          );

          // que pour édition => création : pas possible qu'il y ait des
          // managers
          this.getManagers();
        } else if (this.route.snapshot.url[1].path === 'new') {
          this.edited = false;
          this.organisation = new Organisation();
          this.organisation.country = new Country();
          this.organisation.country.id = null;

          this.organisation.defaultLanguageGui = new Language();
          this.organisation.defaultLanguageGui.id = null;
          this.organisation.defaultLanguagePdf = new Language();
          this.organisation.defaultLanguagePdf.id = null;

          this.organisation.sectorOwner = new User();
          this.organisation.sectorOwner.id = null;
          this.organisation.negoceOwner = new User();
          this.organisation.negoceOwner.id = null;
          this.organisation.hasBeApi = false;
          this.organisation.hasI18n = false;
        }

        if (hasPermission(['ROLE_ADMIN'])) {
          this.getSectorOwners();
        } else {
          // ROLE_SECTOR : les autres
          // ne voient pas cet écran
          this.getSectorOwnerMe();
        }

        this.getNegoceOwners();
        const obs1 = this.listService.getCountries(true);
        const obs2 = this.listService.getLanguages();
        forkJoin([obs1, obs2]).subscribe(
          (data: Array<any>) => {
            this.handleCountries(data[0]);
            this.handleLanguages(data[1]);
            for (const c of this.countries) {
              c.languagesGui = c.languagesGui.sort((a: any, b: any) =>
                this.languagesIndex.get(a['id']).label.localeCompare(this.languagesIndex.get(b['id']).label),
              );
              c.languagesPdf = c.languagesPdf.sort((a: any, b: any) =>
                this.languagesIndex.get(a['id']).label.localeCompare(this.languagesIndex.get(b['id']).label),
              );
            }
          },
          (error) => {
            this.handleGetError(error);
          },
        );
      }
    });
  }

  handleSaveError(error: any) {
    if (error.status === 401) {
      showErrorMessage(this.modal, this.i18nTranslator.translate('distributeur.edit.unauthorized'));
    } else {
      // FIXME détailler les erreurs du formulaire
      // FIXME définir un format pour les erreurs de validation
      console.error(error);
      showErrorMessage(this.modal, this.i18nTranslator.translate('edit.error.generic'));
    }
  }

  handleGetError(error: any) {
    if (error.status === 401) {
      showErrorMessage(this.modal, this.i18nTranslator.translate('distributeur.edit.view.unauthorized')).then((r) => {
        this.router.navigate(['/organisations']);
      });
    } else {
      console.error(error);
      showErrorMessage(this.modal, this.i18nTranslator.translate('error.generic')).then((r) => {
        this.router.navigate(['/organisations']);
      });
    }
  }

  ngOnDestroy() {
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }

  onChangeCountry(countryId: any) {
    countryId = +countryId;
    this.country = this.countries.find((c) => c.id === countryId);

    if (this.organisation.defaultLanguageGui) {
      let hasSameDefaultLanguageGui = false;
      for (const language of this.country.languagesGui) {
        if (language.code == this.organisation.defaultLanguageGui.code) {
          hasSameDefaultLanguageGui = true;
          break;
        }
      }
      if (!hasSameDefaultLanguageGui) {
        this.organisation.defaultLanguageGui = new Language();
        this.organisation.defaultLanguageGui.id = null;
      }
    }

    if (this.organisation.defaultLanguagePdf) {
      let hasSameDefaultLanguagePdf = false;
      for (const language of this.country.languagesPdf) {
        if (language.code == this.organisation.defaultLanguagePdf.code) {
          hasSameDefaultLanguagePdf = true;
          break;
        }
      }
      if (!hasSameDefaultLanguagePdf) {
        this.organisation.defaultLanguagePdf = new Language();
        this.organisation.defaultLanguagePdf.id = null;
      }
    }
  }
}
